import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Circle, DrawingManager, GoogleMap, Polygon } from "@react-google-maps/api";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const DEFAULT_CENTER = { lat: 20, lng: 0 };

const SHAPE_OPTIONS = {
  fillColor: "#ef4444",
  fillOpacity: 0.25,
  strokeColor: "#111827",
  strokeWeight: 2,
  editable: true,
  draggable: true,
};

const CATEGORY_OPTIONS = [
  { value: "loading", label: "Loading" },
  { value: "unloading", label: "Unloading" },
  { value: "parking", label: "Parking" },
  { value: "arriving", label: "Arriving" },
  { value: "departing", label: "Departing" },
  { value: "other", label: "Other" },
];

const TRIP_ACTION_OPTIONS = [
  { value: "nothing", label: "Nothing" },
  { value: "entry", label: "Entry Alert" },
  { value: "exit", label: "Exit Alert" },
  { value: "both", label: "Both" },
];

const toPolygonPoints = (polygon = {}) => {
  const coords = Array.isArray(polygon?.coordinates?.[0]) ? polygon.coordinates[0] : [];
  return coords
    .map((pair) => ({
      lat: Number(pair?.[1]),
      lng: Number(pair?.[0]),
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
};

const buildInitialDraft = (shapeType, geofence) => {
  if (geofence?.type === "circle" && geofence?.center) {
    return {
      type: "circle",
      data: {
        center: {
          lat: Number(geofence.center.lat),
          lng: Number(geofence.center.lng),
        },
        radius: Number(geofence.radius || 100),
      },
    };
  }

  if (geofence?.type === "polygon" && geofence?.polygon) {
    return {
      type: "polygon",
      data: toPolygonPoints(geofence.polygon),
    };
  }

  return null;
};

const arePointsEqual = (left = [], right = []) => {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((point, index) => {
    const other = right[index];
    return (
      Math.abs(Number(point?.lat || 0) - Number(other?.lat || 0)) < 1e-7 &&
      Math.abs(Number(point?.lng || 0) - Number(other?.lng || 0)) < 1e-7
    );
  });
};

const areCircleDraftsEqual = (left, right) => {
  if (!left?.data?.center || !right?.data?.center) return false;
  return (
    Math.abs(Number(left.data.center.lat || 0) - Number(right.data.center.lat || 0)) < 1e-7 &&
    Math.abs(Number(left.data.center.lng || 0) - Number(right.data.center.lng || 0)) < 1e-7 &&
    Math.abs(Number(left.data.radius || 0) - Number(right.data.radius || 0)) < 0.1
  );
};

export default function SharedGeofenceModal({
  open,
  mode = "create",
  initialShapeType = "polygon",
  geofence = null,
  onClose,
  onSubmit,
  saving = false,
}) {
  const [mapsReady, setMapsReady] = useState(false);
  const [drawingMode, setDrawingMode] = useState(initialShapeType);
  const [name, setName] = useState("");
  const [locationLabel, setLocationLabel] = useState("");
  const [category, setCategory] = useState("loading");
  const [enterAction, setEnterAction] = useState("nothing");
  const [exitAction, setExitAction] = useState("nothing");
  const [draft, setDraft] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);

  const mapRef = useRef(null);
  const shapeRef = useRef(null);
  const predictionTimerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted) return;
        setMapsReady(true);
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
      })
      .catch(() => {
        if (mounted) setMapsReady(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const nextDraft = buildInitialDraft(initialShapeType, geofence);
    setDrawingMode(nextDraft?.type || initialShapeType);
    setDraft(nextDraft);
    setName(geofence?.name || "");
    setLocationLabel(geofence?.locationLabel || "");
    setCategory(geofence?.category || "loading");
    setEnterAction(geofence?.enterAction || "nothing");
    setExitAction(geofence?.exitAction || "nothing");

    if (geofence?.type === "circle" && geofence?.center) {
      setMapCenter({
        lat: Number(geofence.center.lat),
        lng: Number(geofence.center.lng),
      });
    } else if (geofence?.type === "polygon") {
      const points = toPolygonPoints(geofence?.polygon);
      if (points[0]) {
        setMapCenter(points[0]);
      } else {
        setMapCenter(DEFAULT_CENTER);
      }
    } else {
      setMapCenter(DEFAULT_CENTER);
    }
    setPlacePredictions([]);
    setShowPredictions(false);
  }, [open, geofence, initialShapeType]);

  useEffect(() => {
    if (!open || !mapsReady) return undefined;
    const raw = String(locationLabel || "").trim();
    const latLngMatch = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!raw || latLngMatch || !autocompleteServiceRef.current) {
      setPlacePredictions([]);
      return undefined;
    }

    if (predictionTimerRef.current) clearTimeout(predictionTimerRef.current);
    predictionTimerRef.current = setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: raw,
          componentRestrictions: { country: "in" },
        },
        (predictions) => {
          setPlacePredictions(Array.isArray(predictions) ? predictions.slice(0, 5) : []);
          setShowPredictions(true);
        }
      );
    }, 180);

    return () => {
      if (predictionTimerRef.current) {
        clearTimeout(predictionTimerRef.current);
        predictionTimerRef.current = null;
      }
    };
  }, [locationLabel, mapsReady, open]);

  useEffect(() => {
    if (!open) return;
    const raw = String(locationLabel || "").trim();
    const match = raw.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!match) return;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const nextCenter = { lat, lng };
    setMapCenter(nextCenter);
    if (mapRef.current) {
      mapRef.current.panTo(nextCenter);
      mapRef.current.setZoom(15);
    }
  }, [locationLabel, open]);

  const handlePredictionSelect = useCallback((prediction) => {
    if (!prediction?.place_id || !geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: prediction.place_id }, (results, status) => {
      if (status !== "OK" || !Array.isArray(results) || !results[0]) return;
      const result = results[0];
      const lat = result.geometry?.location?.lat?.();
      const lng = result.geometry?.location?.lng?.();
      setLocationLabel(result.formatted_address || prediction.description || "");
      setPlacePredictions([]);
      setShowPredictions(false);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const nextCenter = { lat, lng };
        setMapCenter(nextCenter);
        if (mapRef.current) {
          mapRef.current.panTo(nextCenter);
          mapRef.current.setZoom(15);
        }
      }
    });
  }, []);

  const syncShape = useCallback(() => {
    if (!shapeRef.current || !draft) return;

    if (draft.type === "polygon") {
      const path = shapeRef.current.getPath();
      const points = [];
      for (let index = 0; index < path.getLength(); index += 1) {
        points.push({
          lat: path.getAt(index).lat(),
          lng: path.getAt(index).lng(),
        });
      }
      setDraft((current) => {
        if (current?.type === "polygon" && arePointsEqual(current.data, points)) {
          return current;
        }
        return { type: "polygon", data: points };
      });
      return;
    }

    if (draft.type === "circle") {
      const center = shapeRef.current.getCenter();
      const nextDraft = {
        type: "circle",
        data: {
          center: {
            lat: center.lat(),
            lng: center.lng(),
          },
          radius: shapeRef.current.getRadius(),
        },
      };
      setDraft((current) => {
        if (current?.type === "circle" && areCircleDraftsEqual(current, nextDraft)) {
          return current;
        }
        return nextDraft;
      });
    }
  }, [draft]);

  const handleOverlayComplete = useCallback(
    (event) => {
      setDrawingMode(null);
      if (shapeRef.current) {
        shapeRef.current.setMap(null);
      }
      shapeRef.current = event.overlay;

      if (event.type === "polygon") {
        const path = event.overlay.getPath();
        const points = [];
        for (let index = 0; index < path.getLength(); index += 1) {
          points.push({
            lat: path.getAt(index).lat(),
            lng: path.getAt(index).lng(),
          });
        }
        setDraft({ type: "polygon", data: points });
        return;
      }

      if (event.type === "circle") {
        const center = event.overlay.getCenter();
        setDraft({
          type: "circle",
          data: {
            center: {
              lat: center.lat(),
              lng: center.lng(),
            },
            radius: event.overlay.getRadius(),
          },
        });
      }
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (!draft || !String(name || "").trim()) return;

    if (draft.type === "circle") {
      onSubmit?.({
        name: String(name || "").trim(),
        type: "circle",
        center: draft.data.center,
        radius: Number(draft.data.radius || 0),
        locationLabel: String(locationLabel || "").trim(),
        category,
        enterAction,
        exitAction,
        isActive: true,
      });
      return;
    }

    const coordinates = (Array.isArray(draft.data) ? draft.data : [])
      .map((point) => [Number(point.lng), Number(point.lat)])
      .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]));

    if (
      coordinates.length &&
      (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
        coordinates[0][1] !== coordinates[coordinates.length - 1][1])
    ) {
      coordinates.push(coordinates[0]);
    }

    onSubmit?.({
      name: String(name || "").trim(),
      type: "polygon",
      polygon: {
        type: "Polygon",
        coordinates: [coordinates],
      },
      locationLabel: String(locationLabel || "").trim(),
      category,
      enterAction,
      exitAction,
      isActive: true,
    });
  }, [category, draft, enterAction, exitAction, locationLabel, name, onSubmit]);

  const title =
    mode === "edit"
      ? draft?.type === "circle"
        ? "Edit Circular Geofence"
        : "Edit Polygon Geofence"
      : initialShapeType === "circle"
        ? "Add Circular Geofence"
        : "Add Polygon Geofence";

  const canSubmit = Boolean(draft && String(name || "").trim());

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth={false} fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(360px, 40%) 1fr" },
            minHeight: { xs: 700, md: 640 },
          }}
        >
          <Box sx={{ p: 2.5, borderRight: { md: "1px solid #e5e7eb" }, background: "#fff" }}>
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>{title}</Typography>
              <TextField label="Fence Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
              <TextField
                label="Add Location"
                value={locationLabel}
                onChange={(e) => {
                  setLocationLabel(e.target.value);
                  setShowPredictions(true);
                }}
                fullWidth
              />
              {showPredictions && placePredictions.length ? (
                <Box
                  sx={{
                    mt: -1,
                    border: "1px solid #d1d5db",
                    borderRadius: 2,
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 12px 24px rgba(15,23,42,0.12)",
                  }}
                >
                  {placePredictions.map((prediction) => (
                    <Box
                      key={prediction.place_id}
                      onClick={() => handlePredictionSelect(prediction)}
                      sx={{
                        px: 2,
                        py: 1.35,
                        borderBottom: "1px solid #e5e7eb",
                        cursor: "pointer",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": { background: "#f8fafc" },
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                        {prediction.structured_formatting?.main_text || prediction.description}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                        {prediction.structured_formatting?.secondary_text || ""}
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ px: 2, py: 0.75, textAlign: "right", fontSize: "12px", color: "#9ca3af" }}>
                    powered by Google
                  </Box>
                </Box>
              ) : null}
              <TextField select label="Type" value={category} onChange={(e) => setCategory(e.target.value)} fullWidth>
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ fontWeight: 700 }}>Trip Settings</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField select label="Enter" value={enterAction} onChange={(e) => setEnterAction(e.target.value)} fullWidth>
                  {TRIP_ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField select label="Exit" value={exitAction} onChange={(e) => setExitAction(e.target.value)} fullWidth>
                  {TRIP_ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={drawingMode === "polygon" ? "contained" : "outlined"}
                  onClick={() => setDrawingMode("polygon")}
                  fullWidth
                >
                  Polygon
                </Button>
                <Button
                  variant={drawingMode === "circle" ? "contained" : "outlined"}
                  onClick={() => setDrawingMode("circle")}
                  fullWidth
                >
                  Circle
                </Button>
              </Stack>
              <Box sx={{ pt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || saving}>
                  {saving ? "Saving..." : "Submit"}
                </Button>
              </Box>
            </Stack>
          </Box>
          <Box sx={{ p: 2, background: "#f8fafc" }}>
            <Box sx={{ height: "100%", minHeight: { xs: 360, md: 596 }, borderRadius: 3, overflow: "hidden" }}>
              {mapsReady ? (
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={2}
                  onLoad={(map) => {
                    mapRef.current = map;
                    setTimeout(() => {
                      window.google?.maps?.event?.trigger(map, "resize");
                      map.panTo(mapCenter);
                    }, 120);
                  }}
                >
                  <DrawingManager
                    drawingMode={drawingMode}
                    onOverlayComplete={handleOverlayComplete}
                    options={{
                      drawingControl: false,
                      polygonOptions: SHAPE_OPTIONS,
                      circleOptions: SHAPE_OPTIONS,
                    }}
                  />
                  {draft?.type === "polygon" ? (
                    <Polygon
                      paths={draft.data}
                      options={SHAPE_OPTIONS}
                      onLoad={(polygon) => {
                        shapeRef.current = polygon;
                      }}
                      onMouseUp={syncShape}
                      onDragEnd={syncShape}
                    />
                  ) : null}
                  {draft?.type === "circle" ? (
                    <Circle
                      center={draft.data.center}
                      radius={Number(draft.data.radius || 0)}
                      options={SHAPE_OPTIONS}
                      onLoad={(circle) => {
                        shapeRef.current = circle;
                      }}
                      onRadiusChanged={syncShape}
                      onDragEnd={syncShape}
                    />
                  ) : null}
                </GoogleMap>
              ) : (
                <Box sx={{ display: "grid", placeItems: "center", height: "100%", background: "#fff" }}>
                  <Typography sx={{ color: "text.secondary" }}>Loading map...</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
